import { useState, useEffect, lazy, Suspense, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { SEOHead } from '../components/SEOHead';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { getProviderDataBySlug, PROVIDERS_DATA } from '../config/providersData';
import { ProviderPageV2 } from './ProviderPageV2';
import { 
  ArrowLeft,
  ExternalLink, 
  Phone, 
  MapPin, 
  Star,
  TrendingUp,
  Zap,
  Shield,
  Clock,
  Users,
  Award,
  CheckCircle,
  AlertTriangle,
  Calculator,
  Calendar,
  Home,
  Smartphone,
  HeadphonesIcon,
  Globe,
  Info
} from 'lucide-react';

// Polyfill requestIdleCallback pour Safari
const requestIdleCallback = window.requestIdleCallback || function(cb) {
  return setTimeout(cb, 1);
};

// Composant d'image optimisé avec lazy loading
const OptimizedImage = memo(({ src, alt, className, loading = "lazy" }) => {
  return (
    <img 
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      decoding="async"
    />
  );
});

// Skeleton de chargement ultra-léger
const ProviderSkeleton = () => (
  <div className="max-w-lg mx-auto px-4 py-8 text-center animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-6"></div>
    <div className="w-32 h-16 bg-gray-100 rounded-2xl mx-auto mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-6"></div>
    <div className="h-14 bg-green-200 rounded-full w-64 mx-auto"></div>
  </div>
);

export const ProviderPage = () => {
  const { t, i18n } = useTranslation();
  const { providerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [provider, setProvider] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [useV2Design, setUseV2Design] = useState(false);
  
  const isSpanish = i18n.language === 'es';
  const country = provider?.country || 'FR';
  const vertical = provider?.vertical || 'electricity'; 
  const specialist = getSpecialistMessage(country, vertical);
  const saveaPhone = getSaveaPhone(country, vertical, 'LP');
  const saveaPhoneTel = getSaveaPhoneTel(country, vertical, 'LP');

  useEffect(() => {
    // Scroll immédiat
    window.scrollTo(0, 0);
    
    const urlParts = window.location.pathname.split('/').filter(part => part !== '');
    const slug = urlParts[urlParts.length - 1];
    
    const providerData = getProviderDataBySlug(slug);
    
    if (providerData) {
      setProvider(providerData);
      setIsLoading(false);
      
      // Utiliser le design V2 pour FR NRJ, FR BOX, ES NRJ et ES BOX
      const shouldUseV2 = 
        (providerData.country === 'FR' && providerData.vertical === 'electricity') ||
        (providerData.country === 'FR' && providerData.vertical === 'internet') ||
        (providerData.country === 'ES' && providerData.vertical === 'electricity') ||
        (providerData.country === 'ES' && providerData.vertical === 'internet');
      
      setUseV2Design(shouldUseV2);
      
      // Charger les concurrents en différé pour ne pas bloquer le LCP
      requestIdleCallback(() => {
        const { PROVIDERS_DATA } = require('../config/providersData');
        const allProvidersArray = Object.values(PROVIDERS_DATA);
        const sameCountryVertical = allProvidersArray.filter(p => 
          p.country === providerData.country && 
          p.vertical === providerData.vertical && 
          p.name !== providerData.name
        );
        
        const shuffled = sameCountryVertical.sort(() => 0.5 - Math.random());
        setCompetitors(shuffled.slice(0, 6));
      }, { timeout: 1000 });
    } else {
      setIsLoading(false);
    }
  }, [location.pathname, i18n.language]);

  // Afficher skeleton pendant chargement
  if (isLoading) {
    return <ProviderSkeleton />;
  }

  // Utiliser le design V2 si applicable
  if (useV2Design && provider) {
    return <ProviderPageV2 />;
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-sage-900 mb-4">
            {isSpanish ? 'Proveedor no encontrado' : 'Fournisseur non trouvé'}
          </h1>
          <Button asChild className="bg-sage-600 hover:bg-sage-700">
            <Link to={isSpanish ? '/es/luz' : '/fr/comparateur-electricite'}>
              {isSpanish ? 'Ver todos los proveedores' : 'Voir tous les fournisseurs'}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Déterminer le type de CTA selon pays/vertical
  const getCTALabel = () => {
    if (provider.country === 'FR' && provider.vertical === 'electricity') {
      return 'Numéro gratuit';
    } else if (provider.country === 'ES') {
      return 'Atención telefónica';
    }
    return isSpanish ? 'Atención telefónica' : 'Numéro gratuit';
  };

  // Logos concurrents pour desktop (ES NRJ)
  const esNrjLogos = [
    { src: '/images/logos/FAzUPCF.png', alt: 'Endesa' },
    { src: '/images/logos/FAzgKv4.png', alt: 'Iberdrola' },
    { src: '/images/logos/FAzgzGe.png', alt: 'Naturgy' },
    { src: '/images/logos/FAzgYuV.png', alt: 'Repsol' },
  ];

  // Logos concurrents pour desktop (ES BOX)
  const esBoxLogos = [
    { src: '/images/logos/FAzgxn9.png', alt: 'Movistar' },
    { src: '/images/logos/FAzgMFa.png', alt: 'Vodafone' },
    { src: '/images/logos/FA57bZQ.png', alt: 'MásMóvil' },
    { src: '/images/logos/FAzUSpV.png', alt: 'Digi' },
  ];

  // Logos concurrents pour desktop (FR NRJ)
  const frNrjLogos = [
    { src: '/images/logos/FAzUgTB.png', alt: 'EDF' },
    { src: '/images/logos/FAzUZYv.png', alt: 'Engie' },
    { src: '/images/logos/FAzgJGn.png', alt: 'Enedis' },
    { src: '/images/logos/FAzUmTN.png', alt: 'GRDF' },
  ];

  // Logos concurrents pour desktop (FR BOX)
  const frBoxLogos = [
    { src: '/images/logos/FA57iF9.png', alt: 'Orange' },
    { src: '/images/logos/FA57L8u.png', alt: 'Free' },
    { src: '/images/logos/FA57tuj.png', alt: 'Bouygues' },
    { src: '/images/logos/FA57Qyb.png', alt: 'SFR' },
  ];

  // Sélectionner les logos selon pays/vertical
  const getCompetitorLogos = () => {
    if (provider.country === 'FR' && provider.vertical === 'electricity') return frNrjLogos;
    if (provider.country === 'FR' && provider.vertical === 'internet') return frBoxLogos;
    if (provider.country === 'ES' && provider.vertical === 'electricity') return esNrjLogos;
    if (provider.country === 'ES' && provider.vertical === 'internet') return esBoxLogos;
    return frNrjLogos;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        type="provider" 
        provider={provider}
        image={provider?.logo_url}
      />
      
      {/* ABOVE THE FOLD */}
      <section className="pt-8 pb-12 bg-white">
        <div className="max-w-lg mx-auto px-4 text-center">
          
          {/* H1 selon pays et vertical */}
          <h1 className="text-2xl md:text-3xl font-bold mb-6 text-center leading-tight">
            {provider.country === 'FR' && provider.vertical === 'electricity' ? (
              <>
                <span className="text-sage-900">Comparez les </span>
                <span style={{ color: provider.primary_color }}>offres d'électricité</span>
                <span className="text-sage-900"> et réduisez vos factures</span>
              </>
            ) : provider.country === 'ES' && provider.vertical === 'electricity' ? (
              <>
                <span className="text-sage-900">Comparar </span>
                <span style={{ color: provider.primary_color }}>tarifas de luz y gas</span>
                <span className="text-sage-900"> y ahorrar cada mes</span>
              </>
            ) : provider.country === 'ES' && provider.vertical === 'internet' ? (
              <>
                <span className="text-sage-900">Comparamos </span>
                <span style={{ color: provider.primary_color }}>tarifas de fibra y móvil</span>
              </>
            ) : (
              <>
                <span className="text-sage-900">Comparez les </span>
                <span style={{ color: provider.primary_color }}>offres</span>
                <span className="text-sage-900"> et économisez</span>
              </>
            )}
          </h1>
          
          {/* Logo fournisseur avec logos concurrents sur desktop */}
          {/* Ne pas afficher le logo pour Alpiq */}
          {provider.name !== 'Alpiq' && (
            <div className="mb-4">
              <div className="inline-block p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <img 
                  src={provider.logo_url}
                  alt={`Logo ${provider.name}`}
                  className="h-16 w-auto mx-auto object-contain"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
              
              {/* Logos concurrents - Desktop uniquement - Taille normale et en couleurs */}
              <div className="hidden md:flex items-center justify-center gap-4 mt-4">
                {getCompetitorLogos().filter(logo => logo.alt !== provider.name).slice(0, 4).map((logo, index) => (
                  <OptimizedImage 
                    key={index}
                    src={logo.src} 
                    alt={logo.alt} 
                    className="h-10 w-auto" 
                    loading="lazy"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer texte sous le logo */}
          <div className="mb-4 px-2">
            <p className="text-xs text-gray-400 leading-relaxed text-center">
              {provider.country === 'ES' && provider.vertical === 'electricity' ? (
                `SAVEA es un servicio gratuito de comparación de ofertas de energía. Las tarifas mostradas son orientativas y pueden variar. Para obtener un presupuesto personalizado, contacte con nuestros asesores. Servicio gratuito - Llamada no recargada.`
              ) : provider.country === 'ES' && provider.vertical === 'internet' ? (
                `SAVEA es un servicio gratuito de comparación de ofertas de telecomunicaciones. Las tarifas mostradas son orientativas y pueden variar. Para obtener un presupuesto personalizado, contacte con nuestros asesores. Servicio gratuito - Llamada no recargada.`
              ) : provider.country === 'FR' && provider.vertical === 'electricity' ? (
                `SAVEA est un service de comparaison d'offres d'énergie gratuit. Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Pour obtenir un devis personnalisé, contactez nos conseillers. Service gratuit - Appel non surtaxé.`
              ) : (
                `SAVEA est un service de comparaison d'offres internet gratuit. Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Pour obtenir un devis personnalisé, contactez nos conseillers. Service gratuit - Appel non surtaxé.`
              )}
            </p>
          </div>

          {/* CTA PRINCIPAL */}
          <div className="mb-6">
            <div 
              className="rounded-2xl p-6 shadow-lg"
              style={{ background: `linear-gradient(135deg, ${provider.primary_color}, ${provider.primary_color}dd)` }}
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="text-white">
                  <div className="font-bold text-lg">{specialist.cta}</div>
                  <div className="text-white/80 text-sm">{getCTALabel()}</div>
                </div>
              </div>

              <a 
                href={`tel:${saveaPhoneTel}`}
                className="w-full flex flex-col items-center justify-center hover:opacity-90 font-bold text-xl py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-white"
                style={{ backgroundColor: provider.primary_color }}
                data-testid="main-cta-phone"
              >
                <div className="flex items-center">
                  <Phone className="w-6 h-6 mr-3 text-white" />
                  <span className="text-white savea-phone-number">{saveaPhone}</span>
                </div>
                <span className="text-white/70 text-xs mt-1 font-normal">{isSpanish ? 'Número Gratuito' : 'Numéro Gratuit'}</span>
              </a>

              {/* Boutons secondaires */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <a 
                  href={`tel:${saveaPhoneTel}`}
                  className="flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 text-sm"
                  data-testid="cta-souscrire"
                >
                  <Zap className="w-4 h-4" />
                  <span>{isSpanish ? 'Contratar' : 'Souscrire'}</span>
                </a>
                <a 
                  href={`tel:${saveaPhoneTel}`}
                  className="flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 text-sm"
                  data-testid="cta-demenagement"
                >
                  <Home className="w-4 h-4" />
                  <span>{isSpanish ? 'Mudanza' : 'Déménagement'}</span>
                </a>
              </div>

              <div className="text-center mt-4">
                <div className="text-white/90 text-sm">
                  {isSpanish ? 'Lunes a Sábado • 8h-21h' : 'Lundi à Samedi • 8h-21h'}
                </div>
              </div>
            </div>
          </div>

          {/* Présentation positive du fournisseur */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-800 text-sm font-medium mb-2">
                  {provider.vertical === 'electricity' ? (
                    isSpanish 
                      ? `${provider.name} es un proveedor de confianza en el mercado energético.`
                      : `${provider.name} est un fournisseur de confiance sur le marché de l'énergie.`
                  ) : (
                    isSpanish 
                      ? `${provider.name} es un proveedor de confianza en el mercado de las telecomunicaciones.`
                      : `${provider.name} est un fournisseur de confiance sur le marché des télécommunications.`
                  )}
                </p>
                <p className="text-green-700 text-xs">
                  {provider.vertical === 'electricity' ? (
                    isSpanish 
                      ? `Llámenos para comparar las tarifas de ${provider.name} con las ofertas de nuestros socios y encontrar la mejor opción para usted.`
                      : `Appelez-nous pour comparer les tarifs de ${provider.name} avec les offres de nos partenaires et trouver la meilleure option pour vous.`
                  ) : (
                    isSpanish 
                      ? `Llámenos para comparar las tarifas de internet de ${provider.name} con las ofertas de nuestros socios.`
                      : `Appelez-nous pour comparer les offres internet de ${provider.name} avec celles de nos partenaires.`
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer avec numéro fournisseur */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-800 text-sm">
                {isSpanish ? 'Información del Servicio' : 'Informations du Service'}
              </span>
            </div>
            <p className="text-blue-700 text-xs leading-relaxed mb-3">
              {isSpanish 
                ? `SAVEA es un servicio gratuito de comparación de ofertas de energía. Las tarifas mostradas son orientativas y pueden variar. Servicio gratuito - Llamada no recargada. Para contactar directamente al proveedor:`
                : `SAVEA est un service de comparaison d'offres d'énergie gratuit. Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Service gratuit - Appel non surtaxé. Pour contacter directement le fournisseur :`
              }
            </p>
            <div className="bg-white rounded-md p-2 border border-blue-200 text-center">
              <span className="text-blue-800 font-mono text-base font-semibold select-all">{provider.phone_direct}</span>
            </div>
          </div>

          {/* Lien vers site officiel */}
          <div className="text-center mb-6">
            <Button
              variant="outline"
              onClick={() => window.open(provider.website_url, '_blank')}
              className="text-xs border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <ExternalLink className="w-3 h-3 mr-2" />
              {isSpanish ? 'Visitar sitio oficial' : 'Visiter site officiel'}
            </Button>
          </div>

          {/* Tableau des offres - Remonté ici */}
          {provider.offers && provider.offers.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {isSpanish ? 'Ofertas Disponibles' : 'Offres Disponibles'}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow overflow-hidden text-sm">
                  <thead style={{ backgroundColor: provider.primary_color }} className="text-white">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs">{isSpanish ? 'Oferta' : 'Offre'}</th>
                      <th className="px-3 py-2 text-right text-xs">{isSpanish ? 'Precio' : 'Prix'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {provider.offers.slice(0, 3).map((offer, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="px-3 py-2">
                          <span className="font-medium text-gray-900 text-xs">{offer.name}</span>
                          {offer.green && (
                            <Badge className="ml-1 bg-green-100 text-green-800 text-[10px] px-1">
                              {isSpanish ? 'Verde' : 'Vert'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-bold text-gray-900 text-xs">
                            {offer.price_kwh || offer.price || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Caractéristiques principales */}
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            {isSpanish ? 'Características principales' : 'Caractéristiques principales'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {provider.key_features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{feature[isSpanish ? 'es' : 'fr']}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">
            {isSpanish ? 'Acerca de' : 'À propos de'} {provider.name}
          </h2>
          <p className="text-gray-700 leading-relaxed text-center text-sm">
            {provider.description[isSpanish ? 'es' : 'fr']}
          </p>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            {isSpanish ? 'Ventajas Principales' : 'Avantages Principaux'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {provider.advantages.map((advantage, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm">{advantage[isSpanish ? 'es' : 'fr']}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Widget Comparaison - Sans liens vers autres pages fournisseurs */}
      <section className="py-12 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-orange-900 mb-2">
              {isSpanish ? 'Comparar con otros proveedores' : 'Comparer avec d\'autres fournisseurs'}
            </h2>
            <p className="text-orange-700 text-sm">
              {isSpanish 
                ? 'Llámenos para obtener una comparación personalizada'
                : 'Appelez-nous pour obtenir une comparaison personnalisée'
              }
            </p>
          </div>
          
          {/* Liste de comparaison sans liens */}
          <div className="max-w-2xl mx-auto space-y-3">
            {competitors.slice(0, 4).map((comp, index) => (
              <Card key={index} className="bg-white border border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <OptimizedImage 
                          src={comp.logo_url}
                          alt={`${comp.name} logo`}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{comp.name}</h3>
                        <div className="text-xs text-gray-500">
                          {comp.key_features && comp.key_features[0] 
                            ? (isSpanish ? comp.key_features[0].es : comp.key_features[0].fr)
                            : ''
                          }
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-bold text-green-600">{comp.main_price}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* CTA pour appeler */}
          <div className="text-center mt-8">
            <a 
              href={`tel:${saveaPhoneTel}`}
              className="inline-flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all"
            >
              <Phone className="w-5 h-5 mr-2" />
              {isSpanish ? 'Llamar para comparar' : 'Appeler pour comparer'}
            </a>
          </div>
        </div>
      </section>

      {/* Couverture géographique */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">
            {isSpanish ? 'Cobertura Geográfica' : 'Couverture Géographique'}
          </h2>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <MapPin className="w-6 h-6 text-green-600" />
              <div>
                <div className="text-xl font-bold text-green-700">
                  {provider.coverage.cities.toLocaleString()} {isSpanish ? 'municipios' : 'communes'}
                </div>
                <div className="text-green-600 text-sm">{provider.coverage.type[isSpanish ? 'es' : 'fr']}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12" style={{ background: `linear-gradient(135deg, ${provider.primary_color}, ${provider.primary_color}dd)` }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-3">
            {isSpanish ? '¿Necesitas ayuda con tu contrato?' : 'Besoin d\'aide pour votre contrat ?'}
          </h2>
          <p className="text-white/90 mb-6">
            {isSpanish ? 'Habla con nuestros especialistas ahora' : 'Parlez à nos spécialistes maintenant'}
          </p>
          
          <a 
            href={`tel:${saveaPhoneTel}`}
            className="inline-flex items-center justify-center hover:opacity-90 font-bold text-lg py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-white"
            style={{ backgroundColor: provider.primary_color }}
          >
            <Phone className="w-5 h-5 mr-2" />
            <span className="text-white">{saveaPhone}</span>
          </a>
        </div>
      </section>

      {/* Disclaimer final */}
      <section className="py-6 bg-yellow-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-3 text-yellow-800">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed">
              {isSpanish 
                ? 'SAVEA es un servicio gratuito de comparación. No vendemos energía directamente. Te conectamos con especialistas para encontrar las mejores ofertas disponibles. Los precios mostrados son indicativos.'
                : 'SAVEA est un service gratuit de comparaison. Nous ne vendons pas d\'énergie directement. Nous vous mettons en relation avec des spécialistes pour trouver les meilleures offres disponibles. Les prix affichés sont indicatifs.'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
